import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';

import type {GetPaymentServicesResponse, PaymentCard} from '../../../../queries/paymentCard/types';
import {PAYMENT_SERVICES_QUERY_KEY} from '../../../../queries/paymentCard/usePaymentServices';
import Text from '../../../themed/Text';

import type {ApplePayPayment} from './applePay/types';
import PaymentMethodSelector from './PaymentMethodSelector';
import type {ApplePayWebConfig, PaymentMethodType} from './types';

// ============================================
// MOCK PAYMENT SERVICES DATA
// ============================================

/** Mock payment card data */
const mockVisaCard: PaymentCard = {
  id: 'card-visa-4242',
  card_type: 'visa',
  obfuscated_card: '411111******4242',
  expiry_month: 12,
  expiry_year: 2027,
  expired: false,
  test: true,
};

const mockMastercardCard: PaymentCard = {
  id: 'card-mc-8888',
  card_type: 'mastercard',
  obfuscated_card: '555555******8888',
  expiry_month: 6,
  expiry_year: 2026,
  expired: false,
  test: true,
};

const mockAmexCard: PaymentCard = {
  id: 'card-amex-8751',
  card_type: 'amex',
  obfuscated_card: '378282******8751',
  expiry_month: 4,
  expiry_year: 2029,
  expired: false,
  test: true,
};

const mockExpiredCard: PaymentCard = {
  id: 'card-visa-1234',
  card_type: 'visa',
  obfuscated_card: '411111******1234',
  expiry_month: 1,
  expiry_year: 2024,
  expired: true,
  test: true,
};

/** Mock payment services response: all methods, no saved card */
const mockPaymentServicesAllMethods: GetPaymentServicesResponse = {
  services: [
    {
      id: 'svc-till-1',
      type: 'till_payments',
      name: 'Credit Card',
      test: true,
      instruments: true,
      can_tokenize: true,
      saved_card: null,
    },
    {
      id: 'svc-apple-1',
      type: 'apple_pay',
      name: 'Apple Pay',
      test: true,
      instruments: false,
      can_tokenize: true,
      saved_card: null,
    },
    {
      id: 'svc-google-1',
      type: 'google_pay',
      name: 'Google Pay',
      test: true,
      instruments: false,
      can_tokenize: true,
      saved_card: null,
    },
  ],
};

/** Mock payment services response: card + Apple Pay, with saved Visa */
const mockPaymentServicesWithCard: GetPaymentServicesResponse = {
  services: [
    {
      id: 'svc-till-1',
      type: 'till_payments',
      name: 'Credit Card',
      test: true,
      instruments: true,
      can_tokenize: true,
      saved_card: mockVisaCard,
    },
    {
      id: 'svc-apple-1',
      type: 'apple_pay',
      name: 'Apple Pay',
      test: true,
      instruments: false,
      can_tokenize: true,
      saved_card: null,
    },
  ],
};

/** Mock payment services response: card only */
const mockPaymentServicesCardOnly: GetPaymentServicesResponse = {
  services: [
    {
      id: 'svc-till-1',
      type: 'till_payments',
      name: 'Credit Card',
      test: true,
      instruments: true,
      can_tokenize: true,
      saved_card: null,
    },
  ],
};

/** Mock payment services response: with Mastercard */
const mockPaymentServicesWithMastercard: GetPaymentServicesResponse = {
  services: [
    {
      id: 'svc-till-1',
      type: 'till_payments',
      name: 'Credit Card',
      test: true,
      instruments: true,
      can_tokenize: true,
      saved_card: mockMastercardCard,
    },
    {
      id: 'svc-apple-1',
      type: 'apple_pay',
      name: 'Apple Pay',
      test: true,
      instruments: false,
      can_tokenize: true,
      saved_card: null,
    },
    {
      id: 'svc-google-1',
      type: 'google_pay',
      name: 'Google Pay',
      test: true,
      instruments: false,
      can_tokenize: true,
      saved_card: null,
    },
  ],
};

/** Mock payment services response: with Amex */
const mockPaymentServicesWithAmex: GetPaymentServicesResponse = {
  services: [
    {
      id: 'svc-till-1',
      type: 'till_payments',
      name: 'Credit Card',
      test: true,
      instruments: true,
      can_tokenize: true,
      saved_card: mockAmexCard,
    },
    {
      id: 'svc-apple-1',
      type: 'apple_pay',
      name: 'Apple Pay',
      test: true,
      instruments: false,
      can_tokenize: true,
      saved_card: null,
    },
    {
      id: 'svc-google-1',
      type: 'google_pay',
      name: 'Google Pay',
      test: true,
      instruments: false,
      can_tokenize: true,
      saved_card: null,
    },
  ],
};

/** Mock payment services response: with expired card */
const mockPaymentServicesWithExpiredCard: GetPaymentServicesResponse = {
  services: [
    {
      id: 'svc-till-1',
      type: 'till_payments',
      name: 'Credit Card',
      test: true,
      instruments: true,
      can_tokenize: true,
      saved_card: mockExpiredCard,
    },
    {
      id: 'svc-apple-1',
      type: 'apple_pay',
      name: 'Apple Pay',
      test: true,
      instruments: false,
      can_tokenize: true,
      saved_card: null,
    },
    {
      id: 'svc-google-1',
      type: 'google_pay',
      name: 'Google Pay',
      test: true,
      instruments: false,
      can_tokenize: true,
      saved_card: null,
    },
  ],
};

/** Mock payment services response: iOS (card + Apple Pay) */
const mockPaymentServicesIOS: GetPaymentServicesResponse = {
  services: [
    {
      id: 'svc-till-1',
      type: 'till_payments',
      name: 'Credit Card',
      test: true,
      instruments: true,
      can_tokenize: true,
      saved_card: mockVisaCard,
    },
    {
      id: 'svc-apple-1',
      type: 'apple_pay',
      name: 'Apple Pay',
      test: true,
      instruments: false,
      can_tokenize: true,
      saved_card: null,
    },
  ],
};

/** Mock payment services response: Android (card + Google Pay) */
const mockPaymentServicesAndroid: GetPaymentServicesResponse = {
  services: [
    {
      id: 'svc-till-1',
      type: 'till_payments',
      name: 'Credit Card',
      test: true,
      instruments: true,
      can_tokenize: true,
      saved_card: mockVisaCard,
    },
    {
      id: 'svc-google-1',
      type: 'google_pay',
      name: 'Google Pay',
      test: true,
      instruments: false,
      can_tokenize: true,
      saved_card: null,
    },
  ],
};

/**
 * Create a QueryClient decorator with mocked payment services data
 */
const createQueryClientDecorator = (mockData: GetPaymentServicesResponse) => {
  return (Story: React.ComponentType) => {
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
        },
      },
    });

    // Pre-populate the cache with mock data
    queryClient.setQueryData(PAYMENT_SERVICES_QUERY_KEY, mockData);

    return (
      <QueryClientProvider client={queryClient}>
        <Story />
      </QueryClientProvider>
    );
  };
};

const meta = {
  title: 'Kitchen Sink/Payments/PaymentMethodSelector',
  component: PaymentMethodSelector,
  argTypes: {
    selectedMethod: {
      control: 'select',
      options: ['card', 'applePay', 'googlePay'],
    },
    isCardActionLoading: {control: 'boolean'},
  },
  decorators: [
    createQueryClientDecorator(mockPaymentServicesAllMethods),
    Story => (
      <View style={styles.container}>
        <Story />
      </View>
    ),
  ],
} satisfies Meta<typeof PaymentMethodSelector>;

export default meta;

type Story = StoryObj<typeof meta>;

// ============================================
// CARD STATES
// ============================================

export const NoCardSaved: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesCardOnly)],
  args: {
    selectedMethod: 'card',
    onMethodChange: () => {},
    onAddCard: () => console.log('Add card pressed'),
  },
};

export const CardSavedVisa: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesWithCard)],
  args: {
    selectedMethod: 'card',
    onMethodChange: () => {},
    onChangeCard: () => console.log('Change card pressed'),
    onDeleteCard: () => console.log('Delete card pressed'),
  },
};

export const CardSavedMastercard: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesWithMastercard)],
  args: {
    selectedMethod: 'card',
    onMethodChange: () => {},
    onChangeCard: () => console.log('Change card pressed'),
    onDeleteCard: () => console.log('Delete card pressed'),
  },
};

export const CardSavedAmex: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesWithAmex)],
  args: {
    selectedMethod: 'card',
    onMethodChange: () => {},
    onChangeCard: () => console.log('Change card pressed'),
    onDeleteCard: () => console.log('Delete card pressed'),
  },
};

// ============================================
// SELECTION STATES
// ============================================

export const CardSelected: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesWithAmex)],
  args: {
    selectedMethod: 'card',
    onMethodChange: () => {},
    onChangeCard: () => console.log('Change card pressed'),
    onDeleteCard: () => console.log('Delete card pressed'),
  },
};

export const ApplePaySelected: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesWithAmex)],
  args: {
    selectedMethod: 'applePay',
    onMethodChange: () => {},
    onChangeCard: () => console.log('Change card pressed'),
    onDeleteCard: () => console.log('Delete card pressed'),
  },
};

export const GooglePaySelected: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesWithAmex)],
  args: {
    selectedMethod: 'googlePay',
    onMethodChange: () => {},
    onChangeCard: () => console.log('Change card pressed'),
    onDeleteCard: () => console.log('Delete card pressed'),
  },
};

// ============================================
// PLATFORM VARIATIONS
// ============================================

export const MobileIOS: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesIOS)],
  args: {
    selectedMethod: 'card',
    onMethodChange: () => {},
    onChangeCard: () => console.log('Change card pressed'),
    onDeleteCard: () => console.log('Delete card pressed'),
  },
};

export const MobileAndroid: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesAndroid)],
  args: {
    selectedMethod: 'card',
    onMethodChange: () => {},
    onChangeCard: () => console.log('Change card pressed'),
    onDeleteCard: () => console.log('Delete card pressed'),
  },
};

export const WebAllMethods: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesWithCard)],
  args: {
    selectedMethod: 'card',
    onMethodChange: () => {},
    onChangeCard: () => console.log('Change card pressed'),
    onDeleteCard: () => console.log('Delete card pressed'),
  },
};

// ============================================
// LOADING/ERROR STATES
// ============================================

export const Loading: Story = {
  decorators: [
    (Story: React.ComponentType) => {
      const queryClient = new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
          },
        },
      });

      // Don't pre-populate cache to trigger loading state
      return (
        <QueryClientProvider client={queryClient}>
          <Story />
        </QueryClientProvider>
      );
    },
  ],
  args: {
    selectedMethod: 'card',
    onMethodChange: () => {},
  },
};

export const CardActionLoading: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesWithAmex)],
  args: {
    selectedMethod: 'card',
    isCardActionLoading: true,
    onMethodChange: () => {},
    onChangeCard: () => console.log('Change card pressed'),
    onDeleteCard: () => console.log('Delete card pressed'),
  },
};

// ============================================
// EDGE CASES
// ============================================

export const ExpiredCard: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesWithExpiredCard)],
  args: {
    selectedMethod: 'card',
    onMethodChange: () => {},
    onChangeCard: () => console.log('Change card pressed'),
    onDeleteCard: () => console.log('Delete card pressed'),
  },
};

export const CardOnlyNoDigitalWallets: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesCardOnly)],
  args: {
    selectedMethod: 'card',
    onMethodChange: () => {},
    onAddCard: () => console.log('Add card pressed'),
  },
};

// ============================================
// INTERACTIVE DEMO
// ============================================

function InteractiveDemo() {
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodType>('card');
  const [currentMockData, setCurrentMockData] = useState(mockPaymentServicesWithAmex);
  const [isLoading, setIsLoading] = useState(false);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
      },
    },
  });

  // Pre-populate cache with initial mock data
  queryClient.setQueryData(PAYMENT_SERVICES_QUERY_KEY, currentMockData);

  const handleAddCard = () => {
    setIsLoading(true);
    setTimeout(() => {
      const newData = mockPaymentServicesWithCard;
      setCurrentMockData(newData);
      queryClient.setQueryData(PAYMENT_SERVICES_QUERY_KEY, newData);
      setIsLoading(false);
    }, 1500);
  };

  const handleChangeCard = () => {
    setIsLoading(true);
    setTimeout(() => {
      const newData = mockPaymentServicesWithMastercard;
      setCurrentMockData(newData);
      queryClient.setQueryData(PAYMENT_SERVICES_QUERY_KEY, newData);
      setIsLoading(false);
    }, 1500);
  };

  const handleDeleteCard = () => {
    setIsLoading(true);
    setTimeout(() => {
      const newData = mockPaymentServicesAllMethods;
      setCurrentMockData(newData);
      queryClient.setQueryData(PAYMENT_SERVICES_QUERY_KEY, newData);
      setIsLoading(false);
    }, 1000);
  };

  const savedCard = currentMockData.services.find(s => s.type === 'till_payments')?.saved_card;

  return (
    <QueryClientProvider client={queryClient}>
      <View>
        <PaymentMethodSelector
          selectedMethod={selectedMethod}
          onMethodChange={setSelectedMethod}
          onAddCard={handleAddCard}
          onChangeCard={handleChangeCard}
          onDeleteCard={handleDeleteCard}
          isCardActionLoading={isLoading}
        />
        <View style={styles.debugInfo}>
          <Text style={styles.debugText}>Selected: {selectedMethod}</Text>
          <Text style={styles.debugText}>
            Card:{' '}
            {savedCard ? `${savedCard.card_type} ••${savedCard.obfuscated_card.slice(-4)}` : 'None'}
          </Text>
        </View>
      </View>
    </QueryClientProvider>
  );
}

export const Interactive: Story = {
  render: () => <InteractiveDemo />,
};

// ============================================
// ALL VARIANTS SHOWCASE
// ============================================

function AllVariantsDemo() {
  const queryClient1 = new QueryClient({defaultOptions: {queries: {retry: false}}});
  const queryClient2 = new QueryClient({defaultOptions: {queries: {retry: false}}});
  const queryClient3 = new QueryClient({defaultOptions: {queries: {retry: false}}});
  const queryClient4 = new QueryClient({defaultOptions: {queries: {retry: false}}});
  const queryClient5 = new QueryClient({defaultOptions: {queries: {retry: false}}});

  queryClient1.setQueryData(PAYMENT_SERVICES_QUERY_KEY, mockPaymentServicesCardOnly);
  queryClient2.setQueryData(PAYMENT_SERVICES_QUERY_KEY, mockPaymentServicesWithCard);
  queryClient3.setQueryData(PAYMENT_SERVICES_QUERY_KEY, mockPaymentServicesWithAmex);
  queryClient4.setQueryData(PAYMENT_SERVICES_QUERY_KEY, mockPaymentServicesIOS);
  queryClient5.setQueryData(PAYMENT_SERVICES_QUERY_KEY, mockPaymentServicesAndroid);

  return (
    <View style={styles.variantsContainer}>
      <Text style={styles.sectionTitle}>No Card Saved</Text>
      <QueryClientProvider client={queryClient1}>
        <PaymentMethodSelector
          selectedMethod="card"
          onMethodChange={() => {}}
          onAddCard={() => {}}
        />
      </QueryClientProvider>

      <Text style={styles.sectionTitle}>Card Saved - Visa</Text>
      <QueryClientProvider client={queryClient2}>
        <PaymentMethodSelector
          selectedMethod="card"
          onMethodChange={() => {}}
          onChangeCard={() => {}}
          onDeleteCard={() => {}}
        />
      </QueryClientProvider>

      <Text style={styles.sectionTitle}>Card Saved - Apple Pay Selected</Text>
      <QueryClientProvider client={queryClient3}>
        <PaymentMethodSelector
          selectedMethod="applePay"
          onMethodChange={() => {}}
          onChangeCard={() => {}}
          onDeleteCard={() => {}}
        />
      </QueryClientProvider>

      <Text style={styles.sectionTitle}>iOS Only (No Google Pay)</Text>
      <QueryClientProvider client={queryClient4}>
        <PaymentMethodSelector
          selectedMethod="card"
          onMethodChange={() => {}}
          onChangeCard={() => {}}
          onDeleteCard={() => {}}
        />
      </QueryClientProvider>

      <Text style={styles.sectionTitle}>Android Only (No Apple Pay)</Text>
      <QueryClientProvider client={queryClient5}>
        <PaymentMethodSelector
          selectedMethod="card"
          onMethodChange={() => {}}
          onChangeCard={() => {}}
          onDeleteCard={() => {}}
        />
      </QueryClientProvider>
    </View>
  );
}

export const AllVariants: Story = {
  render: () => <AllVariantsDemo />,
};

// ============================================
// APPLE PAY WEB INTEGRATION
// ============================================

/**
 * Mock payment authorization handler.
 * In production, this sends the token to Till Payments.
 */
const mockPaymentAuthorized = async (payment: ApplePayPayment): Promise<boolean> => {
  console.log('[Apple Pay] Payment authorized:', {
    network: payment.token.paymentMethod.network,
    displayName: payment.token.paymentMethod.displayName,
    transactionId: payment.token.transactionIdentifier,
  });
  // Simulate server processing delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  // Return true for success, false for failure
  return true;
};

/**
 * Create mock Apple Pay web config for stories
 */
const createMockApplePayWebConfig = (): ApplePayWebConfig => ({
  serviceId: 'apay-svc-1',
  onPaymentAuthorized: mockPaymentAuthorized,
  onCancel: () => console.log('[Apple Pay] Payment cancelled'),
  onError: error => console.error('[Apple Pay] Error:', error),
});

export const ApplePayWebIntegration: Story = {
  decorators: [createQueryClientDecorator(mockPaymentServicesWithCard)],
  args: {
    selectedMethod: 'applePay',
    onMethodChange: () => {},
    onChangeCard: () => console.log('Change card pressed'),
    onDeleteCard: () => console.log('Delete card pressed'),
    applePayConfig: createMockApplePayWebConfig(),
  },
};

function ApplePayInteractiveDemo() {
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodType>('card');
  const [currentMockData, setCurrentMockData] = useState(mockPaymentServicesWithAmex);
  const [paymentResult, setPaymentResult] = useState<string | null>(null);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
      },
    },
  });

  queryClient.setQueryData(PAYMENT_SERVICES_QUERY_KEY, currentMockData);

  const applePayConfig: ApplePayWebConfig = {
    serviceId: 'apay-svc-1',
    onPaymentAuthorized: async payment => {
      const success = await mockPaymentAuthorized(payment);
      setPaymentResult(success ? 'Payment successful!' : 'Payment failed');
      return success;
    },
    onCancel: () => setPaymentResult('Payment cancelled'),
    onError: error => setPaymentResult(`Error: ${error.message}`),
  };

  const handleAddCard = () => {
    const newData = mockPaymentServicesWithCard;
    setCurrentMockData(newData);
    queryClient.setQueryData(PAYMENT_SERVICES_QUERY_KEY, newData);
  };

  const handleChangeCard = () => {
    const newData = mockPaymentServicesWithMastercard;
    setCurrentMockData(newData);
    queryClient.setQueryData(PAYMENT_SERVICES_QUERY_KEY, newData);
  };

  const handleDeleteCard = () => {
    const newData = mockPaymentServicesWithCard;
    setCurrentMockData(newData);
    queryClient.setQueryData(PAYMENT_SERVICES_QUERY_KEY, newData);
  };

  const savedCard = currentMockData.services.find(s => s.type === 'till_payments')?.saved_card;

  return (
    <QueryClientProvider client={queryClient}>
      <View>
        <PaymentMethodSelector
          selectedMethod={selectedMethod}
          onMethodChange={setSelectedMethod}
          onAddCard={handleAddCard}
          onChangeCard={handleChangeCard}
          onDeleteCard={handleDeleteCard}
          applePayConfig={applePayConfig}
        />
        <View style={styles.debugInfo}>
          <Text style={styles.debugText}>Selected: {selectedMethod}</Text>
          <Text style={styles.debugText}>
            Card:{' '}
            {savedCard ? `${savedCard.card_type} ••${savedCard.obfuscated_card.slice(-4)}` : 'None'}
          </Text>
          {paymentResult ? (
            <Text style={[styles.debugText, {marginTop: 8, fontWeight: '600'}]}>
              Result: {paymentResult}
            </Text>
          ) : null}
        </View>
        <View style={styles.infoBox}>
          <Text style={styles.infoTitle}>Apple Pay Web Integration</Text>
          <Text style={styles.infoText}>
            This demo shows the Apple Pay button when Apple Pay is selected.{'\n\n'}
            <Text style={{fontWeight: '600'}}>Requirements:</Text>
            {'\n'}• Safari browser on macOS or iOS{'\n'}• Apple Pay set up with active cards{'\n'}•
            HTTPS with valid SSL certificate{'\n'}• Verified domain with Apple{'\n\n'}
            <Text style={{fontWeight: '600'}}>In Storybook:</Text>
            {'\n'}
            The actual Apple Pay button only renders in Safari. In other browsers, you will see a
            &quot;not supported&quot; message.
          </Text>
        </View>
      </View>
    </QueryClientProvider>
  );
}

export const ApplePayInteractive: Story = {
  render: () => <ApplePayInteractiveDemo />,
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F5',
    padding: 16,
  },
  variantsContainer: {
    gap: 32,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  debugInfo: {
    marginTop: 24,
    padding: 16,
    backgroundColor: '#E8E8E8',
    borderRadius: 8,
  },
  debugText: {
    fontSize: 14,
    fontFamily: 'monospace',
    color: '#333',
  },
  infoBox: {
    marginTop: 16,
    padding: 16,
    backgroundColor: '#EFF6FF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#BFDBFE',
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E40AF',
    marginBottom: 8,
  },
  infoText: {
    fontSize: 14,
    color: '#1E3A8A',
    lineHeight: 20,
  },
});
